import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container,
  VStack,
  Heading,
  Text,
  Box,
  SimpleGrid,
  useColorMode,
  Avatar,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Badge,
  Divider,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { ArrowForwardIcon } from "@chakra-ui/icons";
import { brandConfig, getColor } from "../../brandConfig";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

interface Entity {
  name: string;
  role: string;
  link: string;
}

const auLeaders: Entity[] = [
  { name: "Chris Bowen", role: "Energy Minister", link: "/energy-research/australia-decision-makers" },
  { name: "Meg O'Neill", role: "Woodside CEO", link: "/energy-research/australia-decision-makers" },
  { name: "Frank Calabria", role: "Origin CEO", link: "/energy-research/australia-decision-makers" },
  { name: "Andrew Forrest", role: "Fortescue Chair", link: "/energy-research/australia-decision-makers" },
  { name: "Angus Taylor", role: "Opposition Energy", link: "/energy-research/australia-decision-makers" },
];

const jpLeaders: Entity[] = [
  { name: "Koichi Hagiuda", role: "Former METI Minister", link: "/energy-research/japan-decision-makers" },
  { name: "METI", role: "Energy Ministry", link: "/energy-research/japan-decision-makers" },
  { name: "JOGMEC", role: "State Reserve Corp", link: "/energy-research/japan-decision-makers" },
  { name: "Eneos (Murofushi)", role: "Largest Refiner", link: "/energy-research/japan-decision-makers" },
  { name: "IEEJ (Tsunoda)", role: "Energy Analyst", link: "/energy-research/japan-decision-makers" },
];

const auPorts: Entity[] = [
  { name: "Port of Brisbane", role: "QLD main fuel import", link: "/energy-research/australia-oil-reserves-by-state" },
  { name: "Geelong (Viva)", role: "VIC refinery port", link: "/energy-research/key-refineries" },
  { name: "Lytton (Ampol)", role: "QLD refinery", link: "/energy-research/key-refineries" },
];

const jpPorts: Entity[] = [
  { name: "Yokohama", role: "JP main import terminal", link: "/energy-research/japan-energy-crisis" },
  { name: "Chiba", role: "Tokyo Bay refinery hub", link: "/energy-research/japan-energy-crisis" },
  { name: "Sakai", role: "Osaka Bay refinery hub", link: "/energy-research/japan-energy-crisis" },
];

const keyRefineries: Entity[] = [
  { name: "S-Oil (Korea)", role: "669k bpd", link: "/energy-research/key-refineries" },
  { name: "GS Caltex (Korea)", role: "800k bpd", link: "/energy-research/key-refineries" },
  { name: "Formosa (Taiwan)", role: "540k bpd", link: "/energy-research/key-refineries" },
  { name: "Phillips 66 (US)", role: "105k bpd", link: "/energy-research/key-refineries" },
];

const supplyRoutes: Entity[] = [
  { name: "US Pacific Route", role: "10-14 days, no chokepoints", link: "/energy-research/alternative-supply-routes" },
  { name: "Cape of Good Hope", role: "25-32 days, no chokepoints", link: "/energy-research/alternative-supply-routes" },
  { name: "Russia ESPO", role: "2-3 days to JP", link: "/energy-research/alternative-supply-routes" },
  { name: "Current Hormuz Route", role: "7-10 days, HIGH RISK", link: "/energy-research/australia-oil-sources" },
];

const EntityCard: React.FC<{ entity: Entity; color: string }> = ({ entity, color }) => {
  const textPrimary = getColor("text.primary", "dark");
  const textSecondary = brandConfig.colors.text.secondary;

  return (
    <Box
      as={RouterLink}
      to={entity.link}
      p={3}
      borderRadius="md"
      border="1px solid"
      borderColor={brandConfig.colors.border.light}
      _hover={{
        borderColor: color,
        shadow: "sm",
        transform: "translateY(-1px)",
        transition: "all 0.2s",
        textDecoration: "none",
      }}
      transition="all 0.2s"
      cursor="pointer"
    >
      <Flex align="center" gap={2}>
        <Avatar name={entity.name} size="xs" bg={color} color="white" />
        <Box>
          <Text fontSize="sm" fontWeight="bold" color={textPrimary}>
            {entity.name}
          </Text>
          <Text fontSize="2xs" color={textSecondary}>
            {entity.role}
          </Text>
        </Box>
      </Flex>
    </Box>
  );
};

const RelationshipSection: React.FC<{
  title: string;
  leftLabel: string;
  rightLabel: string;
  leftEntities: Entity[];
  rightEntities: Entity[];
  leftColor: string;
  rightColor: string;
  connectionLabel: string;
}> = ({
  title,
  leftLabel,
  rightLabel,
  leftEntities,
  rightEntities,
  leftColor,
  rightColor,
  connectionLabel,
}) => {
  const surfaceBg = brandConfig.colors.background.surface;
  const borderLight = brandConfig.colors.border.light;
  const textSecondary = brandConfig.colors.text.secondary;

  return (
    <Box p={6} bg={surfaceBg} borderRadius="lg" border="1px solid" borderColor={borderLight}>
      <Heading size="md" color="#dc2626" mb={4}>
        {title}
      </Heading>
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <VStack align="stretch" spacing={2}>
          <Badge colorScheme="blue" alignSelf="start">{leftLabel}</Badge>
          {leftEntities.map((e) => (
            <EntityCard key={e.name} entity={e} color={leftColor} />
          ))}
        </VStack>

        <Flex align="center" justify="center" direction="column" gap={2}>
          <Icon as={ArrowForwardIcon} color={textSecondary} boxSize={6} />
          <Text fontSize="xs" color={textSecondary} textAlign="center">
            {connectionLabel}
          </Text>
          <Icon as={ArrowForwardIcon} color={textSecondary} boxSize={6} transform="rotate(180deg)" />
        </Flex>

        <VStack align="stretch" spacing={2}>
          <Badge colorScheme="red" alignSelf="start">{rightLabel}</Badge>
          {rightEntities.map((e) => (
            <EntityCard key={e.name} entity={e} color={rightColor} />
          ))}
        </VStack>
      </SimpleGrid>
    </Box>
  );
};

const RelationshipMap: React.FC = () => {
  const { colorMode } = useColorMode();
  const bgMain = getColor("background.main", colorMode);
  const textSecondary = brandConfig.colors.text.secondary;

  return (
    <Box bg={bgMain} minHeight="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={10} flex="1">
        <VStack spacing={6} align="stretch">
          <Breadcrumb fontSize="sm" color={textSecondary}>
            <BreadcrumbItem>
              <BreadcrumbLink as={RouterLink} to="/energy-research">
                Energy Research
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>
              <BreadcrumbLink>Relationship Map</BreadcrumbLink>
            </BreadcrumbItem>
          </Breadcrumb>

          <Heading size="lg" color="#dc2626">
            Relationship Map
          </Heading>
          <Text color={textSecondary} fontSize="sm">
            Click any card to explore that entity's detail page. This map shows how Australia and Japan's
            energy leaders, ports, refineries, and supply routes connect.
          </Text>

          <RelationshipSection
            title="Leaders Network"
            leftLabel="AUSTRALIA"
            rightLabel="JAPAN"
            leftEntities={auLeaders}
            rightEntities={jpLeaders}
            leftColor="#2563eb"
            rightColor="#dc2626"
            connectionLabel="Diplomatic & trade relationships"
          />

          <RelationshipSection
            title="Ports & Terminals"
            leftLabel="AUSTRALIA PORTS"
            rightLabel="JAPAN PORTS"
            leftEntities={auPorts}
            rightEntities={jpPorts}
            leftColor="#2563eb"
            rightColor="#dc2626"
            connectionLabel="Shipping routes & fuel flow"
          />

          <RelationshipSection
            title="Refineries & Routes"
            leftLabel="KEY REFINERIES"
            rightLabel="SUPPLY ROUTES"
            leftEntities={keyRefineries}
            rightEntities={supplyRoutes}
            leftColor="#059669"
            rightColor="#d97706"
            connectionLabel="Refinery output flows via these routes"
          />
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default RelationshipMap;
